import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, TextField } from '@mui/material'
import { FieldProps, FormikProps } from 'formik'

import FormField from 'components/formik/FormField'
import Button from 'components/Button'
import DefaultLayout from 'components/layout/DefaultLayout'
import { bankAccountPath } from 'components/Routes'
import { useAuth } from 'components/AuthProvider'
import { Formik, Field } from 'formik'
import { useMutateCreateBankAccount } from 'hooks/useCompliance'
import * as Yup from 'yup'
import {
  MAX_PROOF_FILE_SIZE_BYTES,
  resolveProofContentType,
  uploadProofFile
} from 'utils/directUpload'
import { useStyles } from './styles'

interface FormValues {
  accountNumber: string
  accountName: string
}

const initialValues: FormValues = {
  accountNumber: '',
  accountName: ''
}

// Format bank account number to XX-XXXX-XXXXXXX-XXX pattern
const formatBankAccountNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbersOnly = value.replace(/\D/g, '')

  // Limit to maximum 16 digits (2+4+7+3)
  const limited = numbersOnly.slice(0, 16)

  // Apply formatting based on length
  if (limited.length <= 2) {
    return limited
  } else if (limited.length <= 6) {
    return `${limited.slice(0, 2)}-${limited.slice(2)}`
  } else if (limited.length <= 13) {
    return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`
  } else {
    return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(
      6,
      13
    )}-${limited.slice(13)}`
  }
}

const validationSchema = Yup.object().shape({
  accountNumber: Yup.string()
    .required('Bank account number is required')
    .matches(
      /^\d{2}-\d{4}-\d{7}-\d{2,3}$/,
      'Please enter a valid bank account number (eg 01-2345-6789012-003)'
    ),
  accountName: Yup.string().required('Account name is required')
})

// Matches the backend allow-list (services/active_storage.py
// PROOF_ALLOWED_CONTENT_TYPES). HEIC/HEIF added so iPhone photos of a statement
// are offered by the file picker; svg is deliberately absent.
const ACCEPTED_PROOF_TYPES =
  'application/pdf,image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/tiff,image/bmp'

export default function AddBankAccount(): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { setToast } = useAuth()
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [proofError, setProofError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createBankAccount = useMutateCreateBankAccount()

  const renderHeader = useCallback(() => {
    return (
      <Typography component='h1' variant='h1' fontSize='4.5rem' color='primary'>
        Manually add a bank account.
      </Typography>
    )
  }, [])

  const handleSubmit = async (values: FormValues) => {
    setProofError(null)
    if (!proofFile) {
      setProofError('Please upload a bank statement')
      return
    }

    setIsSubmitting(true)
    try {
      // Upload the proof document FIRST, then hand its key to the create call so
      // the document is ATTACHED to the bank account. This is an AML control:
      // the proof is the evidence that the payout account belongs to the
      // verified customer, and the admin reviewing the account has to be able to
      // open it. Uploading without passing proof_key back leaves an orphaned
      // blob and no evidence — so a failed upload must NOT create the account.
      const proof = await uploadProofFile(proofFile)
      createBankAccount.mutate(
        {
          account_number: values.accountNumber.trim(),
          account_name: values.accountName.trim(),
          proof_key: proof.key,
          proof_filename: proof.filename
        },
        {
          onSuccess: () => {
            setIsSubmitting(false)
            setToast({
              open: true,
              message:
                "Bank account submitted. We'll review it within 1-2 business days.",
              type: 'success',
              duration: 4000
            })
            navigate(bankAccountPath())
          },
          onError: (error: unknown) => {
            setIsSubmitting(false)
            setToast({
              open: true,
              message:
                error instanceof Error
                  ? error.message
                  : 'Failed to create bank account',
              type: 'error',
              duration: 5000
            })
          }
        }
      )
    } catch (err) {
      setIsSubmitting(false)
      setToast({
        open: true,
        message: err instanceof Error ? err.message : 'Upload failed',
        type: 'error',
        duration: 5000
      })
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setProofError(null)

    if (!file) return

    // Resolved against the backend's allow-list (with an extension fallback for
    // browsers that report no type at all), so an unsupported file is caught
    // here with this message rather than as a 422 on submit.
    if (resolveProofContentType(file) === null) {
      setProofError('Please select a PDF or image file (e.g. PNG, JPEG)')
      return
    }

    if (file.size > MAX_PROOF_FILE_SIZE_BYTES) {
      setProofError(
        `File is too large. Maximum size is ${
          MAX_PROOF_FILE_SIZE_BYTES / 1024 / 1024
        } MB.`
      )
      return
    }

    setProofFile(file)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    return `${(bytes / 1024).toFixed(1)} KB`
  }

  return (
    <DefaultLayout
      wrapperContent={{ mt: 0, mb: 2 }}
      backgroundColor='default'
      heading={renderHeader()}
      showBackIcon
    >
      <Box className={classes.content}>
        <Typography paragraph variant='body1'>
          To keep things safe and secure, all bank accounts are reviewed before
          being linked. Approval usually takes 1-2 business days.
        </Typography>
        <Typography paragraph variant='body1'>
          We'll let you know as soon as it's good to go.
        </Typography>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({
            errors,
            touched,
            setFieldValue,
            values,
            handleSubmit
          }: FormikProps<FormValues>) => (
            <form onSubmit={handleSubmit}>
              <Box className={classes.field}>
                <Field name='accountNumber'>
                  {({ field, meta }: FieldProps<string>) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='Bank Account Number *'
                      placeholder='##-####-#######-###'
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const formatted = formatBankAccountNumber(
                          e.target.value
                        )
                        setFieldValue('accountNumber', formatted)
                      }}
                      value={values.accountNumber}
                    />
                  )}
                </Field>
              </Box>
              <Box className={classes.field}>
                <FormField
                  name='accountName'
                  label='Account Name'
                  placeholder='Account Name'
                  required
                  error={!!(errors.accountName && touched.accountName)}
                />
              </Box>
              {proofFile ? (
                <Box className={classes.proofPreview}>
                  <Box className={classes.proofMeta}>
                    <Typography variant='body1' className={classes.proofName}>
                      {proofFile.name}
                    </Typography>
                    <Typography variant='body1' className={classes.proofSize}>
                      {formatFileSize(proofFile.size)}
                    </Typography>
                  </Box>
                  <Button
                    className={classes.proofBtnChange}
                    type='button'
                    variant='outlined'
                    color='primary'
                    size='small'
                    onClick={() => setProofFile(null)}
                  >
                    Change
                  </Button>
                </Box>
              ) : (
                <Box className={classes.field}>
                  <label className={classes.uploadArea} htmlFor='proof-upload'>
                    <input
                      id='proof-upload'
                      type='file'
                      accept={ACCEPTED_PROOF_TYPES}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    Upload Bank Statement
                  </label>
                </Box>
              )}
              {proofError && (
                <Typography
                  color='error'
                  variant='caption'
                  display='block'
                  sx={{ mb: 1 }}
                >
                  {proofError}
                </Typography>
              )}

              <Button
                type='submit'
                color='primary'
                fullWidth
                loading={isSubmitting}
                className={classes.submitButton}
              >
                Submit bank account
              </Button>
            </form>
          )}
        </Formik>
      </Box>
    </DefaultLayout>
  )
}
